  const TD_REBUY_UPSELL_SELECTORS = {
    button: '[data-td-rebuy-upgrade]',
    bundleButton: '[data-td-bundle-upgrade]',
    bundleMount: '[data-td-bundle-upgrades-mount]',
    variantSwitch: '[data-td-rebuy-variant-switch]',
    discountInput: '.td-rebuy-discount__input',
    discountError: '.td-rebuy-discount__error',
    discountDetails: '.td-rebuy-discount__details',
    orderDetails: '[data-td-order-details]',
    orderDetailsDiscountRemove: '[data-td-order-details-discount-remove]',
  };

  const stripTDRebuyTrailingZeros = (html) => String(html || '').replace(/([.,]00)(?=\s*(?:<\/span>|$))/g, '');

  const getTDRebuyDiscountTotal = (smartCart) => {
    if (typeof smartCart.discountTotal === 'function') {
      const formattedTotal = smartCart.discountTotal();
      if (formattedTotal) return formattedTotal;
    }

    if (typeof smartCart.getCalculatedCartDiscount !== 'function' || typeof smartCart.formatMoney !== 'function') {
      return '';
    }

    const discountAmount = Number(smartCart.getCalculatedCartDiscount());
    if (!Number.isFinite(discountAmount) || discountAmount === 0) return '';

    return smartCart.formatMoney(-Math.abs(discountAmount));
  };

  // Sums the line prices the customer is actually charged, after line-level
  // discounts. Automatic function discounts (for example the first-order
  // subscription promotion) are applied per line and are not itemized in the
  // order-details rows, so summing `original_line_price` here produced a
  // subtotal that did not reconcile with the total beneath it.
  const getTDRebuySubtotal = (smartCart, items) => {
    if (typeof smartCart.formatMoney !== 'function') return '';

    const finalLinePrices = items.map((item) => {
      const finalLinePrice = Number(item?.final_line_price);
      if (Number.isFinite(finalLinePrice)) return finalLinePrice;

      const finalPrice = Number(item?.final_price);
      const quantity = Number(item?.quantity);
      return Number.isFinite(finalPrice) && Number.isFinite(quantity) ? finalPrice * quantity : null;
    });

    if (finalLinePrices.length && finalLinePrices.every((price) => price !== null)) {
      return smartCart.formatMoney(finalLinePrices.reduce((total, price) => total + price, 0));
    }

    if (typeof smartCart.subtotalNumber !== 'function') return '';

    return smartCart.formatMoney(smartCart.subtotalNumber());
  };

  const normalizeTDRebuyVariantId = (value) => String(value || '').split('/').pop();

  const getTDRebuySellingPlanId = (source) => normalizeTDRebuyVariantId(
    source?.selling_plan_allocations?.[0]?.selling_plan_id ||
      source?.selling_plan_allocations?.[0]?.selling_plan?.id ||
      source?.selling_plan_id ||
      source?.selling_plan_allocation?.selling_plan_id ||
      source?.selling_plan_allocation?.selling_plan?.id ||
      source?.selling_plan?.id ||
      source?.selling_plan
  );

  window.TDRebuySubscriptionUpgrade = {
    getTargetVariant(item, targetId) {
      const id = normalizeTDRebuyVariantId(targetId);
      const variants = (item && item.product && item.product.variants) || [];
      return variants.find((variant) => normalizeTDRebuyVariantId(variant.id) === id) || null;
    },

    getSavingsCents(item, targetId) {
      const variant = this.getTargetVariant(item, targetId);
      if (!variant) return 0;

      // Subscription variants carry no variant-level discount: their
      // compare_at_price equals their price, so the original calculation below
      // returned zero for every subscription. The saving lives in the selling
      // plan allocation instead. Allocation prices are already in cents, while
      // variant.price is a major-unit string, hence the different scaling.
      const allocation = variant.selling_plan_allocations && variant.selling_plan_allocations[0];
      if (allocation) {
        const allocationCompareAt = Number(allocation.compare_at_price) || 0;
        const allocationPrice = Number(allocation.price) || 0;
        return Math.max(0, Math.round(allocationCompareAt - allocationPrice));
      }

      const price = parseFloat(variant.price) || 0;
      const compareAtPrice = parseFloat(variant.compare_at_price) || 0;
      return Math.max(0, Math.round((compareAtPrice - price) * 100));
    },

    getIntervalDays(item, targetId) {
      const variant = this.getTargetVariant(item, targetId);
      const match = String((variant && variant.title) || '').match(/(\d+)\s*Month/i);
      return match ? parseInt(match[1], 10) * 30 : 30;
    },


    // BFCM Buy 1, get 1 free: the free box is a separate one-time line that
    // carries the discount. Switching either box to a subscription undoes the
    // offer, so the cart hides the upgrade prompt on that product's one-time
    // lines while a free box is in the cart. Both discount titles ("Collagen:"
    // and "Black Friday:") end in this phrase.
    hasFreeBoxOffer(item) {
      if (!item || item.selling_plan_allocation) return false;

      const items = window.Rebuy?.SmartCart?.items?.() || window.Rebuy?.Cart?.items?.() || [];
      return items.some(
        (line) =>
          String(line.product_id) === String(item.product_id) &&
          !line.selling_plan_allocation &&
          (line.line_level_discount_allocations || []).some((allocation) =>
            /buy 1, get 1 free/i.test(allocation?.discount_application?.title || '')
          )
      );
    },
  };

  class TDRebuyCartUpsellController extends HTMLElement {
    variantSwitches = new Set();
    bundleMutations = new Set();
    bundleRefreshPromise = null;
    orphanNormalizationPromise = null;
    orderDetailsRefreshTimer = null;

    getCartItems = () => window.Rebuy?.SmartCart?.items?.() || window.Rebuy?.Cart?.items?.() || [];

    getCartItem = (lineKey) => this.getCartItems().find((item) => String(item.key) === String(lineKey));

    getReplacementProperties = (properties, overrides = {}) => {
      if (!properties || typeof properties !== 'object' || Array.isArray(properties)) {
        return overrides;
      }

      const { _cart_upsell, _upgraded_from, _switching_from, ...rest } = properties;
      return { ...rest, ...overrides };
    };

    setSwitchControlsBusy = (isBusy) => {
      document
        .querySelectorAll(`${TD_REBUY_UPSELL_SELECTORS.variantSwitch}, ${TD_REBUY_UPSELL_SELECTORS.button}`)
        .forEach((control) => {
          control.disabled = isBusy;
          control.toggleAttribute('aria-busy', isBusy);
        });
      document
        .querySelectorAll(TD_REBUY_UPSELL_SELECTORS.bundleButton)
        .forEach((control) => {
          control.disabled = isBusy;
          control.toggleAttribute('aria-busy', isBusy);
        });
    };

    switchCartLine = async ({
      source,
      item,
      lineKey,
      targetId,
      quantity,
      replacementProperties,
      targetVariant,
      needsSellingPlan,
      successMessage,
    }) => {
      if (
        !lineKey ||
        !/^\d+$/.test(targetId) ||
        !item ||
        String(item.variant_id) === targetId ||
        this.variantSwitches.has(lineKey)
      ) {
        return;
      }

      const previousId = normalizeTDRebuyVariantId(item.variant_id);
      if (!/^\d+$/.test(previousId) || !Number.isInteger(quantity) || quantity < 1) return;

      let addedItem;
      let oldItemRemoved = false;
      this.variantSwitches.add(lineKey);
      this.setSwitchControlsBusy(true);

      try {
        const replacementItem = {
          id: targetId,
          quantity,
          properties: { ...(replacementProperties || {}), _switching_from: previousId },
        };

        if (needsSellingPlan) {
          if (!targetVariant) {
            throw new Error('The selected subscription option is not available right now.');
          }

          const sellingPlanId = getTDRebuySellingPlanId(targetVariant);

          if (!/^\d+$/.test(sellingPlanId)) {
            throw new Error('The selected subscription option is not available right now.');
          }

          replacementItem.selling_plan = sellingPlanId;
        }

        const addResponse = await this.cartRequest('cart/add.js', { items: [replacementItem] });
        addedItem = addResponse.items?.[0];

        if (!addedItem?.key) {
          throw new Error('The replacement product could not be added. Please try again.');
        }

        await this.cartRequest('cart/change.js', { id: lineKey, quantity: 0 });
        oldItemRemoved = true;

        const cleanedProperties = Object.fromEntries(
          Object.entries(addedItem.properties || {}).filter(([key]) => key !== '_switching_from')
        );

        if (!Object.keys(cleanedProperties).length) {
          cleanedProperties._switching_from = '';
        }

        await this.cartRequest('cart/change.js', {
          id: addedItem.key,
          quantity: addedItem.quantity,
          properties: cleanedProperties,
        });

        window.Rebuy?.SmartCart?.updateLiveRegion?.(successMessage);
      } catch (error) {
        if (addedItem?.key && !oldItemRemoved) {
          try {
            const rollbackQuantity = Math.max(0, Number(addedItem.quantity) - quantity);
            await this.cartRequest('cart/change.js', {
              id: addedItem.key,
              quantity: rollbackQuantity,
            });
          } catch (rollbackError) {
            console.error('[td-rebuy-cart-upsells] Variant replacement rollback failed.', rollbackError);
          }
        }

        if (source instanceof HTMLSelectElement) {
          source.value = previousId;
        }
        window.Rebuy?.SmartCart?.updateLiveRegion?.(
          error.message || 'The delivery option could not be updated. Please try again.'
        );
      } finally {
        this.variantSwitches.delete(lineKey);
        this.setSwitchControlsBusy(false);
      }
    };

    connectedCallback() {
      document.addEventListener('click', this.handleClick);
      document.addEventListener('change', this.handleVariantChange);
      document.addEventListener('input', this.handleDiscountInput);
      document.addEventListener('rebuy:cart.change', this.hydrateCards);
      this.observer = new MutationObserver(this.hydrateCards);
      this.observer.observe(document.body, { childList: true, subtree: true });
      this.hydrateCards();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.handleClick);
      document.removeEventListener('change', this.handleVariantChange);
      document.removeEventListener('input', this.handleDiscountInput);
      document.removeEventListener('rebuy:cart.change', this.hydrateCards);
      this.observer?.disconnect();
      clearTimeout(this.orderDetailsRefreshTimer);
      this.orderDetailsRefreshTimer = null;
      this.variantSwitches.clear();
      this.bundleMutations.clear();
    }

    handleDiscountInput = (event) => {
      if (!(event.target instanceof Element) || !event.target.matches(TD_REBUY_UPSELL_SELECTORS.discountInput)) {
        return;
      }

      event.target.closest(TD_REBUY_UPSELL_SELECTORS.discountDetails)?.querySelector(TD_REBUY_UPSELL_SELECTORS.discountError)?.remove();
    };

    handleVariantChange = async (event) => {
      if (!(event.target instanceof Element)) return;

      const select = event.target.closest(TD_REBUY_UPSELL_SELECTORS.variantSwitch);
      if (!(select instanceof HTMLSelectElement) || select.disabled) return;

      const lineKey = select.dataset.tdLineKey;
      const targetId = normalizeTDRebuyVariantId(select.value);
      const item = this.getCartItem(lineKey);

      if (!lineKey || !/^\d+$/.test(targetId) || !item || String(item.variant_id) === targetId) return;

      const quantity = Number(item.quantity);
      const selectedLabel = select.selectedOptions?.[0]?.textContent?.trim() || 'the selected delivery option';
      const itemLabel = item.product_title || item.title || 'Your subscription';
      const targetVariant = window.TDRebuySubscriptionUpgrade.getTargetVariant(item, targetId);

      await this.switchCartLine({
        source: select,
        item,
        lineKey,
        targetId,
        quantity,
        replacementProperties: this.getReplacementProperties(item.properties),
        targetVariant,
        needsSellingPlan: select.selectedIndex !== 0,
        successMessage: `${itemLabel} updated to ${selectedLabel}.`,
      });
    };

    handleClick = async (event) => {
      if (!(event.target instanceof Element)) return;

      const bundleButton = event.target.closest(TD_REBUY_UPSELL_SELECTORS.bundleButton);
      if (bundleButton) {
        await this.handleBundleClick(bundleButton);
        return;
      }

      const discountRemoveButton = event.target.closest(TD_REBUY_UPSELL_SELECTORS.orderDetailsDiscountRemove);
      if (discountRemoveButton) {
        const code = discountRemoveButton.dataset.tdCode;
        if (code) window.Rebuy?.SmartCart?.removeDiscount?.(code);
        return;
      }

      const button = event.target.closest(TD_REBUY_UPSELL_SELECTORS.button);
      if (!button || button.disabled) return;

      const targetId = normalizeTDRebuyVariantId(button.dataset.tdUpgradeId);
      const lineKey = button.dataset.tdLineKey;
      const quantity = Number(button.dataset.tdQuantity);
      const item = this.getCartItem(lineKey);

      if (!/^\d+$/.test(targetId) || !lineKey || !item || !Number.isInteger(quantity) || quantity < 1) return;
      const targetVariant = window.TDRebuySubscriptionUpgrade.getTargetVariant(item, targetId);

      await this.switchCartLine({
        source: button,
        item,
        lineKey,
        targetId,
        quantity,
        replacementProperties: this.getReplacementProperties(item.properties, {
          _cart_upsell: button.dataset.tdUpgradeKind,
          _upgraded_from: button.dataset.tdSourceVariantId,
        }),
        targetVariant,
        needsSellingPlan: true,
        successMessage: `${button.dataset.tdUpgradeTitle || 'The upgrade'} was added to your cart.`,
      });
    };

    handleBundleClick = async (button) => {
      if (button.disabled) return;

      const sourceLineKey = button.dataset.tdBundleSourceLineKey;
      const targetId = normalizeTDRebuyVariantId(button.dataset.tdBundleTargetId);
      const mutationKey = `${sourceLineKey}:${targetId}`;
      const sourceItem = this.getCartItem(sourceLineKey);
      const existingItem = this.getCartItems().find(
        (item) =>
          normalizeTDRebuyVariantId(item.variant_id) === targetId &&
          String(item.properties?._td_bundle_source_line_key) === String(sourceLineKey)
      );

      if (!sourceLineKey || !/^\d+$/.test(targetId) || this.bundleMutations.has(mutationKey)) return;

      this.bundleMutations.add(mutationKey);
      this.setSwitchControlsBusy(true);

      try {
        if (existingItem?.key || button.dataset.tdBundleLineKey) {
          await this.cartRequest('cart/change.js', {
            id: existingItem?.key || button.dataset.tdBundleLineKey,
            quantity: 0,
          });
          window.Rebuy?.SmartCart?.updateLiveRegion?.(`${button.dataset.tdBundleTitle || 'Bundle'} was removed.`);
        } else {
          if (!sourceItem) return;

          const sourceVariantId = normalizeTDRebuyVariantId(sourceItem.variant_id);
          const sourceQuantity = Number(sourceItem.quantity);
          const sellingPlanId = normalizeTDRebuyVariantId(button.dataset.tdBundleTargetSellingPlanId);
          const item = {
            id: targetId,
            quantity: sourceQuantity,
            properties: {
              _td_bundle_upgrade: 'bundle',
              _td_bundle_source_variant_id: sourceVariantId || button.dataset.tdBundleSourceVariantId,
              _td_bundle_source_type: button.dataset.tdBundleGroup,
            },
          };

          if (!/^\d+$/.test(sourceVariantId) || !Number.isInteger(sourceQuantity) || sourceQuantity < 1) {
            throw new Error('The original product could not be replaced. Please try again.');
          }

          if (button.dataset.tdBundleGroup === 'subscription') {
            if (!/^\d+$/.test(sellingPlanId)) {
              throw new Error('The subscription bundle is not available right now.');
            }
            item.selling_plan = sellingPlanId;
          }

          let addedItem;
          let sourceRemoved = false;

          try {
            const addResponse = await this.cartRequest('cart/add.js', { items: [item] });
            addedItem = addResponse.items?.[0];

            if (!addedItem?.key) {
              throw new Error('The bundle could not be added. Please try again.');
            }

            await this.cartRequest('cart/change.js', {
              id: sourceLineKey,
              quantity: 0,
            });
            sourceRemoved = true;
          } catch (error) {
            if (addedItem?.key && !sourceRemoved) {
              try {
                const rollbackQuantity = Math.max(0, Number(addedItem.quantity) - sourceQuantity);
                await this.cartRequest('cart/change.js', {
                  id: addedItem.key,
                  quantity: rollbackQuantity,
                });
              } catch (rollbackError) {
                console.error('[td-rebuy-cart-upsells] Bundle replacement rollback failed.', rollbackError);
              }
            }

            throw error;
          }
        }
      } catch (error) {
        window.Rebuy?.SmartCart?.updateLiveRegion?.(
          error.message || 'The bundle could not be updated. Please try again.'
        );
      } finally {
        this.bundleMutations.delete(mutationKey);
        this.setSwitchControlsBusy(false);
      }
    };

    setTDRebuyContent = (el, html, { isText = false } = {}) => {
      if (!el) return;
      const key = isText ? 'textContent' : 'innerHTML';
      if (el[key] === html) return;
      el[key] = html;
    };

    scheduleOrderDetailsRefresh = () => {
      clearTimeout(this.orderDetailsRefreshTimer);
      this.orderDetailsRefreshTimer = setTimeout(() => {
        this.orderDetailsRefreshTimer = null;
        this.renderOrderDetails();
      }, 0);
    };

    renderOrderDetails = () => {
      const root = document.querySelector(TD_REBUY_UPSELL_SELECTORS.orderDetails);
      const sc = window.Rebuy?.SmartCart;
      if (!root || !sc) return;

      const hasItems = typeof sc.hasItems === 'function' && sc.hasItems();
      if (root.hidden !== !hasItems) root.hidden = !hasItems;
      if (!hasItems) return;

      const subtotalEl = root.querySelector('[data-td-order-details-subtotal]');
      if (subtotalEl) {
        this.setTDRebuyContent(
          subtotalEl,
          stripTDRebuyTrailingZeros(getTDRebuySubtotal(sc, this.getCartItems()))
        );
      }

      const discountRow = root.querySelector('[data-td-order-details-discount-row]');
      const showDiscount = typeof sc.shouldShowDiscountSubtotal === 'function' && sc.shouldShowDiscountSubtotal();
      if (discountRow) {
        if (discountRow.hidden !== !showDiscount) discountRow.hidden = !showDiscount;

        if (showDiscount) {
          const codes = (typeof sc.getAppliedDiscountCodes === 'function' && sc.getAppliedDiscountCodes()) || [];
          const code = codes[0] || '';
          const tagEl = discountRow.querySelector('[data-td-order-details-discount-tag]');
          this.setTDRebuyContent(tagEl, code, { isText: true });

          const removeEl = discountRow.querySelector(TD_REBUY_UPSELL_SELECTORS.orderDetailsDiscountRemove);
          if (removeEl) removeEl.dataset.tdCode = code;

          const amountEl = discountRow.querySelector('[data-td-order-details-discount-amount]');
          if (amountEl) {
            this.setTDRebuyContent(amountEl, stripTDRebuyTrailingZeros(getTDRebuyDiscountTotal(sc)));
          }
        }
      }

      const totalEl = root.querySelector('[data-td-order-details-total]');
      if (totalEl && typeof sc.subtotal === 'function') {
        this.setTDRebuyContent(totalEl, stripTDRebuyTrailingZeros(sc.subtotal()));
      }

      const cartItems = this.getCartItems();
      const allItemsAreSubscriptions =
        cartItems.length > 0 && cartItems.every((item) => Boolean(item?.selling_plan_allocation));
      const shippingEl = root.querySelector('[data-td-order-details-shipping]');
      this.setTDRebuyContent(
        shippingEl,
        allItemsAreSubscriptions ? 'FREE' : 'Calculated at checkout',
        { isText: true }
      );
    };

    hydrateCards = (event) => {
      this.setSwitchControlsBusy(this.variantSwitches.size > 0);
      this.renderOrderDetails();
      if (event?.type === 'rebuy:cart.change') this.scheduleOrderDetailsRefresh();
      const shouldNormalizeOrphans = !event || event.type === 'rebuy:cart.change';

      if (!shouldNormalizeOrphans) {
        this.refreshBundleUpgrades(false);
        return;
      }

      this.normalizeOrphanedBundleLines({ items: this.getCartItems() })
        .then(() => this.refreshBundleUpgrades(event?.type === 'rebuy:cart.change'))
        .catch((error) => {
          console.error('[td-rebuy-cart-upsells] Orphaned bundle lines could not be normalized.', error);
          return this.refreshBundleUpgrades(event?.type === 'rebuy:cart.change');
        });
    };

    refreshBundleUpgrades = async (force = false) => {
      const mounts = [...document.querySelectorAll(TD_REBUY_UPSELL_SELECTORS.bundleMount)];
      if (!mounts.length) return;

      if (
        this.bundleRefreshPromise ||
        (!force && mounts.every((mount) => ['loading', 'loaded'].includes(mount.dataset.tdBundleUpgradeState)))
      ) {
        return this.bundleRefreshPromise;
      }

      mounts.forEach((mount) => {
        mount.dataset.tdBundleUpgradeState = 'loading';
      });

      this.bundleRefreshPromise = this.fetchBundleUpgradeSection()
        .then((sectionMarkup) => {
          const parsedDocument = new DOMParser().parseFromString(sectionMarkup, 'text/html');
          const section = parsedDocument.querySelector('[data-td-bundle-upgrades-section]');

          if (!section) throw new Error('The bundle upgrade section could not be rendered.');

          mounts.forEach((mount) => {
            const group = [...section.querySelectorAll('[data-td-bundle-upgrades-group]')].find(
              (candidate) => candidate.dataset.tdBundleUpgradesGroup === mount.dataset.tdBundleUpgradesMount
            );
            const hasCards = Boolean(group?.querySelector('[data-td-bundle-upgrade-card]'));

            mount.replaceChildren();
            mount.hidden = !hasCards;

            if (group) {
              [...group.childNodes].forEach((node) => mount.append(node.cloneNode(true)));
            }

            mount.dataset.tdBundleUpgradeState = 'loaded';
          });
        })
        .catch((error) => {
          mounts.forEach((mount) => mount.removeAttribute('data-td-bundle-upgrade-state'));
          console.error('[td-rebuy-cart-upsells] Bundle upgrades could not be rendered.', error);
        })
        .finally(() => {
          this.bundleRefreshPromise = null;
        });

      return this.bundleRefreshPromise;
    };

    async fetchBundleUpgradeSection() {
      const url = new URL(`${window.Shopify.routes.root}cart`, window.location.origin);
      url.searchParams.set('section_id', 'td-rebuy-bundle-upgrades');
      const response = await fetch(url, { cache: 'no-store' });

      if (!response.ok) {
        throw new Error('The bundle upgrade section could not be loaded.');
      }

      return response.text();
    }

    async normalizeOrphanedBundleLines(cart) {
      if (this.orphanNormalizationPromise) return this.orphanNormalizationPromise;

      this.orphanNormalizationPromise = (async () => {
        const items = Array.isArray(cart?.items) ? cart.items : [];
        const lineKeys = new Set(items.map((item) => String(item.key)));
        const orphanedItems = items.filter(
          (item) => {
            const sourceLineKey = item.properties?._td_bundle_source_line_key;
            return (
              item.properties?._td_bundle_upgrade === 'bundle' &&
              sourceLineKey &&
              !lineKeys.has(String(sourceLineKey))
            );
          }
        );

        let normalizedCart = null;

        for (const item of orphanedItems) {
          const properties = Object.fromEntries(
            Object.entries(item.properties || {}).filter(
              ([key]) =>
                !['_td_bundle_upgrade', '_td_bundle_source_line_key', '_td_bundle_source_variant_id', '_td_bundle_source_type'].includes(key)
            )
          );

          if (!Object.keys(properties).length) properties._td_bundle_upgrade = '';

          normalizedCart = await this.cartRequest('cart/change.js', {
            id: item.key,
            quantity: item.quantity,
            properties,
          });
        }

        return normalizedCart;
      })().finally(() => {
        this.orphanNormalizationPromise = null;
      });

      return this.orphanNormalizationPromise;
    }

    async cartRequest(path, body) {
      const response = await fetch(`${window.Shopify.routes.root}${path}`, {
        method: body ? 'POST' : 'GET',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.description || result.message || 'The cart could not be updated. Please try again.');
      }

      return result;
    }
  }

  if (!customElements.get('td-rebuy-cart-upsell-controller')) {
    customElements.define('td-rebuy-cart-upsell-controller', TDRebuyCartUpsellController);
  }

  const mountTDRebuyCartUpsellController = () => {
    if (!document.querySelector('td-rebuy-cart-upsell-controller')) {
      document.body.append(document.createElement('td-rebuy-cart-upsell-controller'));
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountTDRebuyCartUpsellController, { once: true });
  } else {
    mountTDRebuyCartUpsellController();
  }
